// The entire audio identity of Hexline: one sound (GDD §15).
//
// A short, soft, slightly liquid "pop" on pellet consumption, synthesized
// with the Web Audio API (no asset file): a sine blip with a fast pitch drop
// and exponential decay. Playback pitch scales with current speed —
// roughly 0.9× at minSpeed up to 1.5× at maxSpeed (GDD §15.1).

import { tuning } from '@evox/shared';

let ctx: AudioContext | null = null;
let muted = false;
let volume = 0.5;

function ensureContext(): AudioContext | null {
  if (ctx === null) {
    try {
      ctx = new AudioContext();
    } catch {
      return null;
    }
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

/** Call once from a user-gesture handler to unlock audio on iOS/Safari. */
export function unlockAudio(): void {
  ensureContext();
}

export function setMuted(m: boolean): void {
  muted = m;
}

export function setVolume(v: number): void {
  volume = Math.max(0, Math.min(1, v));
}

/**
 * The pellet-eat pop. `speed` is the local player's current speed in
 * edges/s; it maps linearly to playback rate 0.9 → 1.5 (GDD §15.1).
 * `pan` ∈ [−1, 1]: subtle stereo position of the pellet relative to the player.
 */
export function playPelletEat(speed: number, pan = 0): void {
  if (muted) return;
  const ac = ensureContext();
  if (ac === null) return;

  const ratio = (speed - tuning.minSpeed) / (tuning.maxSpeed - tuning.minSpeed);
  const rate = 0.9 + 0.6 * Math.max(0, Math.min(1, ratio));

  const t0 = ac.currentTime;
  const dur = 0.16;

  const osc = ac.createOscillator();
  osc.type = 'sine';
  // A soft liquid pop: quick downward pitch sweep.
  osc.frequency.setValueAtTime(620 * rate, t0);
  osc.frequency.exponentialRampToValueAtTime(180 * rate, t0 + dur);

  const gain = ac.createGain();
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(0.35 * volume, t0 + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

  const panner = ac.createStereoPanner();
  panner.pan.setValueAtTime(Math.max(-1, Math.min(1, pan)) * 0.5, t0);

  osc.connect(gain).connect(panner).connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}
